import {IEventCatalogueDomainStore} from "../../domainStore/pages/eventCatalogueDomainStore";
import {action, computed, makeObservable, observable, runInAction} from "mobx";
import {IModalStore} from "../global/modalStore";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import {IEventsDomainStore} from "../../domainStore/pages/eventsDomainStore";
import {IEventsUiStore} from "./eventsUiStore";
import {EventCatalogueSortField} from "../../../shared/enums/eventCatalogueSortField";
import {StickyBooleanObservable} from "../../observables/stickyBooleanObservable";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {EventCalendarType} from "../../../shared/enums/eventCalendarType";
import {hasEnhancedFeatures} from "../../../shared/eventCalendarTypeHelper";
import {EventTemplateDetail} from "../../domainStore/pages/models/eventTemplateDetail";
import {IVillageCalendarsDomainStore} from "../../domainStore/pages/villageCalendarsDomainStore";

export interface IEventCatalogueUiStore {
  /**
   * Indicating that the event catalogue list is loading.
   */
  isLoading: boolean;
  /**
   * Indicating that the event catalogue template is loading.
   */
  isEditLoading: boolean;
  /**
   * Load event templates, with optional search term
   */
  loadEventCatalogue: (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<void>;
  /**
   * Set the status for an event template
   */
  setEventTemplateStatus: (
    id: string,
    status: EventTemplateStatus
  ) => Promise<void>;
  /**
   * ID of a category that is currently expanded.
   */
  expandedCategoryId: string;
  /**
   * Sort event template in the selected category.
   */
  sortEventTemplates: (
    categoryId: string,
    sortField: string,
    sortOrder: string
  ) => void;
  /**
   * Reset event template categories.
   */
  resetEventTemplateCategories: () => void;
  /**
   * Reload the editable event template.
   */
  reloadEditableEventTemplate: (eventTemplateId: string) => Promise<void>;
  /**
   * Load the selected event category and update isModalLoading accordingly.
   */
  loadSelectedEventCategory: (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType
  ) => Promise<void>;
  /**
   * True if the modal is open.
   */
  isModalOpen: boolean;
  /**
   * Load event template by ID.
   */
  loadEventTemplate: (id: string) => Promise<void>;
  /**
   * The last search term used for searching
   */
  eventCatalogueSearchText: string;
  /**
   * Boolean holding template body loading state
   */
  isEventTemplateBodyLoading: StickyBooleanObservable;
  /**
   * Minimum number of result per category.
   */
  minSearchResultLimit: number;
  /**
   * Maximum number of result per category.
   * This is used to display all templates for a selected category.
   */
  maxSearchResultLimit: number;

  /**
   * A function to search event templates.
   */
  searchTemplates: (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm: string,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<void>;

  /**
   * A function to sort search result.
   */
  sortCatalogueSearchResult: (sortField: string, sortOrder: SortOrder) => void;

  /**
   * True if it's in search mode.
   */
  isSearch: boolean;

  /**
   * A function to trigger reload search result event.
   */
  reloadCatalogueSearchResult: () => void;

  /**
   * Act as a reload event that the event catalogue search result table is listening to.
   */
  reloadCatalogueSearchResultEvent: string;

  /**
   * A function to reset search related data.
   */
  resetSearch: () => void;

  /**
   * Load all event categories.
   */
  loadEventCategories: () => Promise<void>;

  /**
   * Details of the selected event template.
   */
  eventTemplateDetail?: EventTemplateDetail;

  /**
   * Clear event template detail.
   */
  resetEventTemplateDetail: () => void;

  /**
   * True if a catalogue with the full (Enhanced) feature set, i.e. Enhanced or Wellbeing, is selected
   */
  isEnhancedCatalogue: boolean;

  /**
   * The name of the selected catalogue
   */
  selectedCatalogueName: string;
}

export class EventCatalogueUiStore implements IEventCatalogueUiStore {
  isCatalogueLoading = false;
  isSearchLoading = false;
  isEditLoading: boolean;
  isModalOpen: boolean;
  expandedCategoryId: string;
  eventCatalogueSearchText: string = "";
  isEventTemplateBodyLoading: StickyBooleanObservable;
  isSearch: boolean;
  reloadCatalogueSearchResultEvent = "";
  eventTemplateDetail?: EventTemplateDetail;

  readonly minSearchResultLimit = 3;
  readonly maxSearchResultLimit = 9999;
  private eventCatalogueDomainStore: IEventCatalogueDomainStore;
  private modalStore: IModalStore;
  private eventsDomainStore: IEventsDomainStore;
  private eventsUiStore: IEventsUiStore;
  private villageCalendarsDomainStore?: IVillageCalendarsDomainStore;
  // Only the latest request of each kind may clear its loading flag.
  private catalogueLoadingRequestId = 0;
  private searchLoadingRequestId = 0;
  private bodyLoadingRequestId = 0;
  selectedCatalogueName = "";

  constructor(
    eventsDomainStore: IEventsDomainStore,
    eventsUiStore: IEventsUiStore,
    eventCatalogueDomainStore: IEventCatalogueDomainStore,
    modalStore: IModalStore,
    villageCalendarsDomainStore?: IVillageCalendarsDomainStore
  ) {
    makeObservable(this, {
      isLoading: computed,
      isCatalogueLoading: observable,
      isSearchLoading: observable,
      isEditLoading: observable,
      isModalOpen: observable,
      expandedCategoryId: observable,
      eventCatalogueSearchText: observable,
      isEventTemplateBodyLoading: observable,
      isSearch: observable,
      reloadCatalogueSearchResultEvent: observable,
      eventTemplateDetail: observable,
      resetEventTemplateDetail: action,
      selectedCatalogueName: observable
    });
    this.isModalOpen = false;
    this.isEditLoading = false;
    this.expandedCategoryId = "";
    this.isSearch = false;

    this.modalStore = modalStore;
    this.eventCatalogueDomainStore = eventCatalogueDomainStore;
    this.eventsDomainStore = eventsDomainStore;
    this.eventsUiStore = eventsUiStore;
    this.villageCalendarsDomainStore = villageCalendarsDomainStore;
    this.eventTemplateDetail = undefined;

    // This is to ensure the loading spinner is displayed for at least 0.5s.
    this.isEventTemplateBodyLoading = new StickyBooleanObservable(
      false,
      0,
      500
    );
  }

  reloadCatalogueSearchResult = () => {
    runInAction(() => {
      this.reloadCatalogueSearchResultEvent = new Date().toISOString();
    });
  };
  loadSelectedEventCategory = async (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType
  ): Promise<void> => {
    this.isEventTemplateBodyLoading.setValue(true);
    try {
      await this.eventCatalogueDomainStore.loadEventTemplateCategory(
        categoryId,
        status,
        calendarType
      );
    } catch (error) {
      this.modalStore.openErrorModal(error as Error);
    }
    this.isEventTemplateBodyLoading.setValue(false);
  };

  loadEventCatalogue = async (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => {
    const isBodySearch = searchTerm !== undefined;
    const requestId = isBodySearch
      ? ++this.bodyLoadingRequestId
      : ++this.catalogueLoadingRequestId;
    if (isBodySearch) {
      this.isEventTemplateBodyLoading.setValue(true);
    } else {
      runInAction(() => {
        this.isCatalogueLoading = true;
      });
    }

    try {
      await this.eventCatalogueDomainStore.loadEventTemplateCategories(
        status,
        maxTemplates,
        sortField,
        searchTerm,
        showDynamicCategories,
        calendarType,
        abortController
      );
    } catch (error) {
    } finally {
      if (isBodySearch) {
        if (requestId === this.bodyLoadingRequestId) {
          this.isEventTemplateBodyLoading.setValue(false);
        }
      } else if (requestId === this.catalogueLoadingRequestId) {
        runInAction(() => {
          this.isCatalogueLoading = false;
        });
      }
    }
  };

  setEventTemplateStatus = async (id: string, status: EventTemplateStatus) => {
    await this.eventCatalogueDomainStore.setEventTemplateStatus(id, status);
  };

  sortEventTemplates = (
    categoryId: string,
    sortField: string,
    sortOrder: string
  ) => {
    this.eventCatalogueDomainStore.sortEventTemplates(
      categoryId,
      sortField,
      sortOrder
    );
  };

  resetEventTemplateCategories = () => {
    runInAction(() => (this.expandedCategoryId = ""));
    this.eventCatalogueDomainStore.resetEventTemplateCategories();
  };

  reloadEditableEventTemplate = async (
    eventTemplateId: string
  ): Promise<void> => {
    runInAction(() => {
      this.isEditLoading = true;
    });
    try {
      await this.eventCatalogueDomainStore.loadEventTemplate(eventTemplateId);
      await this.eventCatalogueDomainStore.loadEventTemplateImage();
    } catch (error) {
      this.modalStore.openErrorModal(error as Error);
    }
    runInAction(() => {
      this.isEditLoading = false;
    });
  };

  loadEventTemplate = async (id: string): Promise<void> => {
    runInAction(() => {
      this.eventsUiStore.isLoading = true;
    });
    // Ensure the CMS calendar → personTypeIds lookup is populated before
    // we default the event's audience. Non-fatal: if it fails, we fall
    // back to the integration API's single VillageCalendar.personType.
    if (
      this.villageCalendarsDomainStore &&
      this.villageCalendarsDomainStore.villageCalendars.length === 0
    ) {
      try {
        await this.villageCalendarsDomainStore.getVillageCalendars();
      } catch {
        // Swallow: the fallback path handles missing CMS data.
      }
    }
    const eventDetails =
      await this.eventCatalogueDomainStore.createEventFromTemplate(id);
    const calendar = this.eventsDomainStore.getCalendarByName(
      this.eventsUiStore.selectedCalendarName
    );
    if (calendar) {
      // CMS-first, integration fallback: the full personTypeIds set the
      // selected calendar covers. Supports mixed audiences (e.g. IA + SA).
      const calendarPersonTypeIds =
        this.eventsUiStore.personTypeIdsBySelectedCalendarName;
      eventDetails.personTypes =
        calendarPersonTypeIds && calendarPersonTypeIds.length > 0
          ? [...calendarPersonTypeIds]
          : [calendar.personType];
    }
    if (this.eventsDomainStore.startDate) {
      eventDetails.eventDateDetails.startDate.setDate(
        this.eventsDomainStore.startDate
      );

      eventDetails.eventDateDetails.endDate.setDate(
        new Date(
          this.eventsDomainStore.startDate.getTime() +
            60000 *
              ((eventDetails.templateDurationHour || 0) * 60 +
                (eventDetails.templateDurationMin || 0))
        )
      );
      if (
        hasEnhancedFeatures(
          this.eventsUiStore.calendarTypeBySelectedCalendarName
        )
      ) {
        eventDetails.eventDateDetails.startDate.setIsDirty(false);
        eventDetails.eventDateDetails.setIsDirty(false);
      }

      eventDetails.eventDateDetails.endDate.setIsDirty(false);
    }

    runInAction(() => {
      this.eventsDomainStore.editableEvent = eventDetails;
    });

    await this.eventsDomainStore.getEventCategories(
      this.eventsUiStore.calendarTypeBySelectedCalendarName
    );
    await this.eventsDomainStore.getEventLocations();
    await this.eventsDomainStore.getEventImage();
    await this.eventsDomainStore.getVillages(
      this.eventsUiStore.selectedCalendarName
    );
    this.eventsUiStore.isLoading = false;
  };

  searchTemplates = async (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm: string,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ): Promise<void> => {
    const requestId = ++this.searchLoadingRequestId;
    runInAction(() => {
      this.isSearchLoading = true;
      this.eventCatalogueSearchText = searchTerm;
    });
    try {
      await this.eventCatalogueDomainStore.loadEventTemplates(
        status,
        sortField,
        searchTerm,
        calendarType,
        abortController
      );
    } finally {
      if (requestId === this.searchLoadingRequestId) {
        runInAction(() => {
          this.isSearchLoading = false;
        });
      }
    }
  };

  sortCatalogueSearchResult = (sortField: string, sortOrder: SortOrder) => {
    this.eventCatalogueDomainStore.sortCatalogueSearchResult(
      sortField,
      sortOrder
    );
  };

  resetSearch = () => {
    runInAction(() => {
      this.isSearch = false;
      this.eventCatalogueSearchText = "";
    });
  };

  loadEventCategories = () =>
    this.eventCatalogueDomainStore.loadEventCategories(
      this.eventsUiStore.selectedCalendarType
    );

  resetEventTemplateDetail = () => {
    this.eventTemplateDetail = undefined;
  };

  get isLoading(): boolean {
    return this.isSearch ? this.isSearchLoading : this.isCatalogueLoading;
  }

  get isEnhancedCatalogue(): boolean {
    return (
      this.selectedCatalogueName.includes(EventCalendarType.Enhanced) ||
      this.selectedCatalogueName.includes(EventCalendarType.Wellbeing)
    );
  }
}
